import { CascadedShadowGenerator, Scene } from '@babylonjs/core';
import { type PhysicsWorld } from '@roadworld/physics';
import type { ChunkPayload } from '@roadworld/shared';
import { fetchChunk } from '../net/api.js';
import { Chunk } from './Chunk.js';
import {
  chebyshev,
  chunkCenterLatLng,
  chunkCenterWorld,
  chunkKey,
  chunksInRing,
  worldToChunkCoord,
  type ChunkCoord,
  type SessionFrame,
} from './coords.js';
import type { RoadDetail, SurfaceKind } from './surface.js';

export interface ChunkManagerOptions {
  /** Vertices per side for terrain meshes. */
  terrainResolution: number;
  /** Active ring radius — chunks within this Chebyshev distance are loaded. */
  activeRadius: number;
  /** Cached ring radius — chunks beyond this are disposed. activeRadius <= cachedRadius. */
  cachedRadius: number;
  /** DEM grid resolution for each chunk fetch. */
  demResolution: number;
}

/** 3x3 active (radius 1), 7x7 cached (radius 3). The cached ring is wider than active
 *  so prefetchAhead can warm 1–2 chunks past the active ring without them being disposed
 *  on the next update(). */
export const DEFAULT_CHUNK_OPTIONS: ChunkManagerOptions = {
  terrainResolution: 128,
  activeRadius: 1,
  cachedRadius: 3,
  demResolution: 64,
};

/** Client-side concurrent-fetch cap. Set to match the server's Overpass slot count so
 *  prioritization isn't undone by chunks queuing on the server. */
const MAX_CONCURRENT_FETCHES = 2;

type LoadState = 'queued' | 'inflight';

interface LoadingChunk {
  coord: ChunkCoord;
  abort: AbortController;
  /** Resolves when the chunk is fully built or has been cancelled. Never rejects — callers
   *  (currently just ensureChunkAt) treat both outcomes as "we're done waiting", and the
   *  presence of the chunk in `this.chunks` is the source of truth for success. */
  promise: Promise<void>;
  resolveLoad: () => void;
  state: LoadState;
}

export class ChunkManager {
  private scene: Scene;
  private physics: PhysicsWorld;
  private shadowGenerator: CascadedShadowGenerator | null;
  private frame: SessionFrame | null = null;
  private opts: ChunkManagerOptions;
  private chunks = new Map<string, Chunk>();
  /** Chunks that are either queued for fetch or actively being fetched. */
  private loading = new Map<string, LoadingChunk>();
  private inFlight = 0;
  /** Most recent player position + forward direction — used to score the queue. */
  private playerX = 0;
  private playerZ = 0;
  private forwardX = 0;
  private forwardZ = 1;
  private lastCenter: ChunkCoord | null = null;
  private lastPrefetchCenter: ChunkCoord | null = null;
  private onChunkReady?: (c: ChunkCoord) => void;
  private onChunkDispose?: (c: ChunkCoord) => void;
  private onChunkPayload?: (c: ChunkCoord, payload: ChunkPayload) => void;
  /** When false, all chunks render at high LOD regardless of ring position. Toggled from
   *  the HUD so the user can A/B compare perf with and without LOD. */
  private lodEnabled = true;
  /** Origin chunk payload fetched ahead of a session switch (see prefetchOrigin) so the
   *  outgoing area can stay rendered during the network wait. Consumed by startFetch once
   *  the matching frame is made active via reset(). */
  private prefetchedOrigin: { frame: SessionFrame; payload: ChunkPayload } | null = null;

  constructor(
    scene: Scene,
    physics: PhysicsWorld,
    opts: ChunkManagerOptions = DEFAULT_CHUNK_OPTIONS,
    shadowGenerator: CascadedShadowGenerator | null = null,
  ) {
    this.scene = scene;
    this.physics = physics;
    this.opts = opts;
    this.shadowGenerator = shadowGenerator;
  }

  /** Reset for a new session, anchored at `origin`. Unloads all current chunks. */
  reset(frame: SessionFrame): void {
    for (const c of this.chunks.values()) {
      this.onChunkDispose?.(c.coord);
      c.dispose();
    }
    this.chunks.clear();
    for (const l of this.loading.values()) l.abort.abort();
    this.loading.clear();
    this.inFlight = 0;
    this.frame = frame;
    this.lastCenter = null;
    this.lastPrefetchCenter = null;
  }

  /**
   * Fetch the origin (0,0) chunk payload for `frame` WITHOUT disposing the current session
   * or mutating `this.frame`. This lets the caller keep the outgoing area on screen during
   * the network wait; afterwards `reset(frame)` + `ensureChunkAt(0,0)` builds it instantly
   * from this cache. The SAME `frame` object must be passed to `reset` for the cache to be
   * consumed (matched by reference in startFetch).
   */
  async prefetchOrigin(frame: SessionFrame, signal?: AbortSignal): Promise<void> {
    const coord = worldToChunkCoord(0, 0, frame.chunkSize);
    const centerLL = chunkCenterLatLng(coord, frame);
    const payload = await fetchChunk({
      lat: centerLL.lat,
      lng: centerLL.lng,
      size: frame.chunkSize,
      demRes: this.opts.demResolution,
      signal,
    });
    if (signal?.aborted) return;
    this.prefetchedOrigin = { frame, payload };
  }

  setOnChunkReady(cb: (c: ChunkCoord) => void): void {
    this.onChunkReady = cb;
  }

  /** Notified when a chunk is about to be disposed (out of cached ring or session reset).
   *  Traffic manager hooks here to despawn cars belonging to the chunk. */
  setOnChunkDispose(cb: (c: ChunkCoord) => void): void {
    this.onChunkDispose = cb;
  }

  /** Notified with each chunk's raw payload right after fetch (before mesh build).
   *  Used by area-entry notifications to harvest admin-boundary polygons. */
  setOnChunkPayload(cb: (c: ChunkCoord, payload: ChunkPayload) => void): void {
    this.onChunkPayload = cb;
  }

  isReady(): boolean {
    return this.frame !== null;
  }

  /** Master switch for ring-based LOD. When false, every chunk renders at high detail.
   *  Applies the new selection to every loaded chunk immediately so the user sees the
   *  perf difference in the HUD on the next frame. */
  setLodEnabled(enabled: boolean): void {
    if (this.lodEnabled === enabled) return;
    this.lodEnabled = enabled;
    this.applyLodToAllChunks();
  }

  isLodEnabled(): boolean {
    return this.lodEnabled;
  }

  /** Re-evaluate the LOD level of every loaded chunk based on the current ring center and
   *  toggle state. Called when the toggle changes and after each ring shift. */
  private applyLodToAllChunks(): void {
    const center = this.lastCenter;
    for (const chunk of this.chunks.values()) {
      const inActiveRing =
        center !== null && chebyshev(chunk.coord, center) <= this.opts.activeRadius;
      const level = !this.lodEnabled || inActiveRing ? 'high' : 'low';
      chunk.setLOD(level);
    }
  }

  /** Read-only view of the session frame (origin + chunk size). */
  getFrame(): SessionFrame | null {
    return this.frame;
  }

  /**
   * Snapshot of every chunk the manager currently tracks, with its load status. Used by
   * the minimap to overlay the streaming state. `queued` and `inflight` both surface as
   * `pending` so the minimap doesn't need to know about the queue.
   */
  getChunkStates(
    centerWorldX: number,
    centerWorldZ: number,
  ): Array<{ coord: ChunkCoord; status: 'active' | 'cached' | 'pending' }> {
    if (!this.frame) return [];
    const center = worldToChunkCoord(centerWorldX, centerWorldZ, this.frame.chunkSize);
    const out: Array<{ coord: ChunkCoord; status: 'active' | 'cached' | 'pending' }> = [];
    for (const chunk of this.chunks.values()) {
      const inActiveRing = chebyshev(chunk.coord, center) <= this.opts.activeRadius;
      out.push({ coord: chunk.coord, status: inActiveRing ? 'active' : 'cached' });
    }
    for (const l of this.loading.values()) {
      out.push({ coord: l.coord, status: 'pending' });
    }
    return out;
  }

  /** True if the chunk at the given world position is active. */
  hasActiveAt(worldX: number, worldZ: number): boolean {
    if (!this.frame) return false;
    const c = worldToChunkCoord(worldX, worldZ, this.frame.chunkSize);
    return this.chunks.get(chunkKey(c))?.isActive() ?? false;
  }

  /** Iterate every built chunk currently tracked. The impact system uses this to
   *  provision per-tree colliders for nearby chunks. */
  activeChunks(): IterableIterator<Chunk> {
    return this.chunks.values();
  }

  /** Look up a built chunk by its `"cx,cz"` key. */
  getChunkByKey(key: string): Chunk | undefined {
    return this.chunks.get(key);
  }

  /** True if the chunk containing (worldX, worldZ) is already built and active. */
  hasChunkAt(worldX: number, worldZ: number): boolean {
    if (!this.frame) return false;
    const c = worldToChunkCoord(worldX, worldZ, this.frame.chunkSize);
    return this.chunks.get(chunkKey(c))?.isActive() ?? false;
  }

  /** Get the height at a world position, looking it up in whichever chunk contains it. */
  sampleHeight(worldX: number, worldZ: number): number | null {
    if (!this.frame) return null;
    const c = worldToChunkCoord(worldX, worldZ, this.frame.chunkSize);
    const chunk = this.chunks.get(chunkKey(c));
    if (!chunk) return null;
    return chunk.sampleWorldHeight(worldX, worldZ);
  }

  /** Sample the landcover color (RGB 0..1) at a world position — used for particle tinting. */
  sampleCoverColor(worldX: number, worldZ: number): [number, number, number] | null {
    if (!this.frame) return null;
    const c = worldToChunkCoord(worldX, worldZ, this.frame.chunkSize);
    const chunk = this.chunks.get(chunkKey(c));
    if (!chunk) return null;
    return chunk.sampleCoverColor(worldX, worldZ);
  }

  /**
   * Classify the surface at a world position (road / path / grass). Returns null if the
   * containing chunk hasn't been built yet — callers should treat that as "unknown" and
   * fall back to the default (road) multiplier so the vehicle doesn't lurch while loading.
   */
  classifySurface(worldX: number, worldZ: number, margin = 0): SurfaceKind | null {
    if (!this.frame) return null;
    const c = worldToChunkCoord(worldX, worldZ, this.frame.chunkSize);
    const chunk = this.chunks.get(chunkKey(c));
    if (!chunk) return null;
    return chunk.classifyWorldSurface(worldX, worldZ, margin);
  }

  /**
   * Look up the OSM road metadata at a world position (class, lanes, etc.). Returns null
   * when off-road or when the containing chunk isn't loaded. Used by the road-class debug
   * HUD; the per-frame cost is one hash lookup plus a tiny segment-distance check.
   */
  classifyRoadDetail(worldX: number, worldZ: number): RoadDetail | null {
    if (!this.frame) return null;
    const c = worldToChunkCoord(worldX, worldZ, this.frame.chunkSize);
    const chunk = this.chunks.get(chunkKey(c));
    if (!chunk) return null;
    return chunk.classifyWorldRoadDetail(worldX, worldZ);
  }

  /**
   * Drive streaming. Call each frame with the player's world position. Loads chunks within
   * activeRadius, unloads beyond cachedRadius. Cheap — only does ring-shift work when the
   * player crosses a chunk boundary.
   */
  update(worldX: number, worldZ: number): void {
    if (!this.frame) return;
    this.playerX = worldX;
    this.playerZ = worldZ;
    const center = worldToChunkCoord(worldX, worldZ, this.frame.chunkSize);
    if (
      this.lastCenter &&
      this.lastCenter.cx === center.cx &&
      this.lastCenter.cz === center.cz
    ) {
      return;
    }
    this.lastCenter = center;

    // Enqueue anything in the active ring that we don't have.
    for (const coord of chunksInRing(center, this.opts.activeRadius)) {
      this.enqueueFetch(coord);
    }

    // Unload anything beyond the cached ring.
    for (const [key, chunk] of [...this.chunks]) {
      if (chebyshev(chunk.coord, center) > this.opts.cachedRadius) {
        this.onChunkDispose?.(chunk.coord);
        chunk.dispose();
        this.chunks.delete(key);
      }
    }
    for (const [key, l] of [...this.loading]) {
      if (chebyshev(l.coord, center) > this.opts.cachedRadius) {
        l.abort.abort();
        if (l.state === 'inflight') this.inFlight--;
        this.loading.delete(key);
        l.resolveLoad();
      }
    }

    // Re-evaluate LOD for every remaining chunk — the player crossed a chunk boundary, so
    // chunks that were in the active ring last frame may now be cached and vice versa.
    this.applyLodToAllChunks();

    this.pumpQueue();
  }

  /**
   * Velocity-aware prefetch. Requests up to two chunks beyond the active ring along the
   * player's forward direction so they're already cached by the time the player gets there.
   * Fires once per chunk boundary, plus stores the forward vector for priority scoring.
   */
  prefetchAhead(worldX: number, worldZ: number, forwardX: number, forwardZ: number): void {
    if (!this.frame) return;
    const lenSq = forwardX * forwardX + forwardZ * forwardZ;
    if (lenSq < 1e-4) return;
    const invLen = 1 / Math.sqrt(lenSq);
    const fx = forwardX * invLen;
    const fz = forwardZ * invLen;

    // Stash for priority scoring even if we don't actually enqueue anything this call.
    this.playerX = worldX;
    this.playerZ = worldZ;
    this.forwardX = fx;
    this.forwardZ = fz;
    this.pumpQueue();

    const center = worldToChunkCoord(worldX, worldZ, this.frame.chunkSize);
    if (
      this.lastPrefetchCenter &&
      this.lastPrefetchCenter.cx === center.cx &&
      this.lastPrefetchCenter.cz === center.cz
    ) {
      return;
    }
    this.lastPrefetchCenter = center;

    const chunkSize = this.frame.chunkSize;
    for (let n = this.opts.activeRadius + 1; n <= this.opts.activeRadius + 2; n++) {
      const target = worldToChunkCoord(
        worldX + fx * chunkSize * n,
        worldZ + fz * chunkSize * n,
        chunkSize,
      );
      this.enqueueFetch(target);
    }
    this.pumpQueue();
  }

  /** Force-load the chunk at the given world position and wait for it (used at session start). */
  async ensureChunkAt(worldX: number, worldZ: number): Promise<void> {
    if (!this.frame) throw new Error('ChunkManager.reset() must be called first');
    const c = worldToChunkCoord(worldX, worldZ, this.frame.chunkSize);
    const key = chunkKey(c);
    if (this.chunks.get(key)?.isActive()) return;
    // Seed priority origin so this chunk wins the queue immediately.
    this.playerX = worldX;
    this.playerZ = worldZ;
    this.enqueueFetch(c);
    this.pumpQueue();
    const loading = this.loading.get(key);
    if (loading) await loading.promise;
  }

  /** Insert a chunk into the queue if it isn't already loaded or loading. Does NOT pump. */
  private enqueueFetch(coord: ChunkCoord): void {
    const key = chunkKey(coord);
    if (this.chunks.has(key) || this.loading.has(key)) return;
    let resolveLoad!: () => void;
    const promise = new Promise<void>((res) => {
      resolveLoad = res;
    });
    this.loading.set(key, {
      coord,
      abort: new AbortController(),
      promise,
      resolveLoad,
      state: 'queued',
    });
  }

  /**
   * Dispatch queued fetches up to the concurrency cap, picking the highest-priority queued
   * chunk each iteration. Priority: closest to the player wins; forward chunks tiebreak over
   * side/back at equal distance.
   */
  private pumpQueue(): void {
    while (this.inFlight < MAX_CONCURRENT_FETCHES) {
      let bestKey: string | null = null;
      let bestScore = -Infinity;
      for (const [key, l] of this.loading) {
        if (l.state !== 'queued') continue;
        const score = this.scoreFetch(l.coord);
        if (score > bestScore) {
          bestScore = score;
          bestKey = key;
        }
      }
      if (!bestKey) break;
      const next = this.loading.get(bestKey)!;
      next.state = 'inflight';
      this.inFlight++;
      this.startFetch(next);
    }
  }

  /**
   * Higher = more important. The dominant term is the player's distance to the chunk's
   * *bounding box*, not its center — so a chunk you're hugging the edge of (say, the west
   * neighbor when you're on the west side of your current chunk) scores higher than a
   * chunk one full step away (the north neighbor at chunk-center distance). Forward
   * direction is a small tiebreaker so chunks ahead of you win ties.
   */
  private scoreFetch(coord: ChunkCoord): number {
    if (!this.frame) return 0;
    const chunkSize = this.frame.chunkSize;
    const half = chunkSize * 0.5;
    const cx = coord.cx * chunkSize;
    const cz = coord.cz * chunkSize;
    // Closest point of the chunk's bbox to the player. Yields 0 if the player is inside it.
    const clampedX = clamp(this.playerX, cx - half, cx + half);
    const clampedZ = clamp(this.playerZ, cz - half, cz + half);
    const edgeDist = Math.hypot(this.playerX - clampedX, this.playerZ - clampedZ);
    // Forward bias uses chunk-center direction so it's stable even when edgeDist = 0.
    const centerDx = cx - this.playerX;
    const centerDz = cz - this.playerZ;
    const centerDist = Math.hypot(centerDx, centerDz);
    const forwardDot =
      centerDist > 1e-3 ? (centerDx * this.forwardX + centerDz * this.forwardZ) / centerDist : 0;
    return -edgeDist * 100 + forwardDot;
  }

  private startFetch(loading: LoadingChunk): void {
    if (!this.frame) return;
    const frame = this.frame;
    const coord = loading.coord;
    const key = chunkKey(coord);
    void (async () => {
      const centerLL = chunkCenterLatLng(coord, frame);
      try {
        // Use the prefetched origin payload if it was fetched for this exact frame (avoids a
        // redundant network round-trip after a session switch — see prefetchOrigin).
        const pre = this.prefetchedOrigin;
        let payload: ChunkPayload;
        if (pre && pre.frame === frame && coord.cx === 0 && coord.cz === 0) {
          payload = pre.payload;
          this.prefetchedOrigin = null;
        } else {
          payload = await fetchChunk({
            lat: centerLL.lat,
            lng: centerLL.lng,
            size: frame.chunkSize,
            demRes: this.opts.demResolution,
            signal: loading.abort.signal,
          });
        }
        // Bail if disposed or evicted (cancelled by update() going out of range) during the fetch.
        if (this.frame !== frame || this.loading.get(key) !== loading) return;
        const chunk = new Chunk(
          this.scene,
          this.physics,
          coord,
          frame.chunkSize,
          this.shadowGenerator,
        );
        chunk.build(payload, { terrainResolution: this.opts.terrainResolution });
        this.chunks.set(key, chunk);
        this.onChunkPayload?.(coord, payload);
        // Pick the right LOD for where this chunk landed in the ring — without this, every
        // newly-fetched cached-ring chunk would render at high LOD until the next ring shift.
        const inActiveRing =
          this.lastCenter !== null && chebyshev(coord, this.lastCenter) <= this.opts.activeRadius;
        chunk.setLOD(!this.lodEnabled || inActiveRing ? 'high' : 'low');
        this.onChunkReady?.(coord);
      } catch (err) {
        if ((err as { name?: string } | undefined)?.name === 'AbortError') {
          // Aborted because the chunk went out of range — silent.
        } else {
          console.warn(`[chunk ${key}] fetch failed`, err);
        }
      } finally {
        const stillTracked = this.loading.get(key) === loading;
        if (stillTracked) {
          this.loading.delete(key);
          this.inFlight--;
          loading.resolveLoad();
          this.pumpQueue();
        }
      }
    })();
  }
}

function clamp(v: number, lo: number, hi: number): number {
  return v < lo ? lo : v > hi ? hi : v;
}
